import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Phone, Award, Clock, Users, Shield, Home, Layers, Paintbrush, Droplets } from "lucide-react";
import { Button } from "@/components/ui/button";
import LazyImage from "@/components/LazyImage";

const ServicesPage = () => {
  // 6 services: rénovation intérieure, plafonnage, carrelage, peinture, salle de bain, pose de sol
  const services = [
    {
      id: "renovation-interieure",
      slug: "renovation-interieure-bruxelles",
      icon: Home,
      title: "Rénovation Intérieure",
      subtitle: "Transformation Complète",
      description:
        "Projet de rénovation globale à Bruxelles ? Perfect Team Renov coordonne l'ensemble des travaux pour une transformation complète de votre intérieur. Un seul interlocuteur du début à la fin : de la conception aux finitions, nous gérons votre projet de A à Z.",
      longDescription:
        "Que ce soit pour une rénovation partielle ou une transformation complète de votre appartement ou maison à Uccle, Ixelles, Waterloo ou Rhode-Saint-Genèse, nous garantissons un travail soigné, un chantier propre et des délais respectés.",
      features: [
        "Rénovation complète d'appartements",
        "Transformation de maisons",
        "Coordination tous corps de métier",
        "Respect des délais et budget",
        "Chantier toujours propre",
        "Garantie décennale",
      ],
      image: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800",
      gallery: [
        "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=400",
        "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=400",
      ],
      color: "from-orange-600 to-red-500",
    },
    {
      id: "plafonnage",
      slug: "plafonnage-bruxelles",
      icon: Layers,
      title: "Plafonnage",
      subtitle: "Enduits et Finitions Murales",
      description:
        "Plafonneurs professionnels à Bruxelles. Nous réalisons tous types d'enduits intérieurs avec une précision millimétrique : plâtre traditionnel, enduit de lissage, crépi décoratif. Nos artisans expérimentés préparent vos murs pour une finition parfaite.",
      longDescription:
        "Intervention dans toute la région bruxelloise et ses environs : Uccle, Ixelles, Waterloo, Rhode-Saint-Genèse, Sint-Pieters-Leeuw. Du simple rafraîchissement au traitement de murs très abîmés, nous avons la solution.",
      features: [
        "Plâtre traditionnel et moderne",
        "Enduit de lissage haute qualité",
        "Réparation de fissures et défauts",
        "Préparation parfaite pour peinture",
        "Finition lisse ou texturée",
        "Chantier rendu propre",
      ],
      image: "https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=800",
      gallery: [
        "https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=400",
        "https://images.unsplash.com/photo-1674830564825-8828719d2341?w=400",
      ],
      color: "from-amber-500 to-orange-500",
    },
    {
      id: "carrelage",
      slug: "carrelage-bruxelles",
      icon: Layers,
      title: "Carrelage",
      subtitle: "Pose Professionnelle",
      description:
        "Carreleur professionnel à Bruxelles. Nous posons tous types de carrelage avec précision et savoir-faire : sols, murs, salles de bain, cuisines. Grands formats, mosaïque, carreaux de ciment – notre équipe maîtrise toutes les techniques.",
      longDescription:
        "De la faïence murale au carrelage grand format 120x60, nous garantissons des joints parfaitement alignés et un niveau impeccable. Intervention à Bruxelles, Uccle, Ixelles, Waterloo et toutes les communes environnantes.",
      features: [
        "Carrelage sol et mur",
        "Grands formats (60x60, 80x80, 120x60)",
        "Faïence salle de bain et cuisine",
        "Mosaïque et carreaux de ciment",
        "Précision millimétrique",
        "Joints parfaitement alignés",
      ],
      image: "https://images.unsplash.com/photo-1600566752355-35792bedcfea?w=800",
      gallery: [
        "https://images.unsplash.com/photo-1600566752355-35792bedcfea?w=400",
        "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=400",
      ],
      color: "from-emerald-500 to-teal-500",
    },
    {
      id: "peinture",
      slug: "peinture-interieure-bruxelles",
      icon: Paintbrush,
      title: "Peinture Intérieure",
      subtitle: "Couleurs et Finitions",
      description:
        "Peintres professionnels à Bruxelles. Transformez vos espaces avec notre service de peinture de haute qualité. Murs, plafonds, boiseries – nous maîtrisons toutes les techniques pour un résultat impeccable et durable.",
      longDescription:
        "Protection soignée de vos meubles et sols, préparation minutieuse des surfaces, application en 2 couches minimum. Intervention dans toute la région : Uccle, Ixelles, Waterloo, Rhode-Saint-Genèse.",
      features: [
        "Peinture murs et plafonds",
        "Laque sur boiseries et portes",
        "Préparation surfaces soignée",
        "Protection maximale du mobilier",
        "Peintures premium durables",
        "Conseil couleur personnalisé",
      ],
      image: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800",
      gallery: [
        "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=400",
        "https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=400",
      ],
      color: "from-blue-500 to-cyan-500",
    },
    {
      id: "salle-de-bain",
      slug: "salle-de-bain-bruxelles",
      icon: Droplets,
      title: "Rénovation Salle de Bain",
      subtitle: "Votre Espace Bien-être",
      description:
        "Rénovation salle de bain à Bruxelles. Transformez votre salle de bain en un véritable espace bien-être. Douche à l'italienne, carrelage, sanitaires – nous coordonnons tous les corps de métier pour une rénovation clé en main.",
      longDescription:
        "Étanchéité garantie, travail soigné jusque dans les moindres détails et chantier toujours propre malgré les travaux de démolition. Intervention à Uccle, Ixelles, Waterloo, Rhode-Saint-Genèse.",
      features: [
        "Douche à l'italienne sur mesure",
        "Remplacement baignoire par douche",
        "Carrelage murs et sols",
        "Étanchéité parfaite garantie",
        "Installation sanitaires",
        "Rénovation complète en 2-3 semaines",
      ],
      image: "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=800",
      gallery: [
        "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=400",
        "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=400",
      ],
      color: "from-cyan-500 to-blue-500",
    },
    {
      id: "pose-sol",
      slug: "pose-sol-bruxelles",
      icon: Layers,
      title: "Pose de Sol",
      subtitle: "Parquet et Revêtements",
      description:
        "Poseurs de sol professionnels à Bruxelles. Parquet massif, contrecollé, stratifié ou vinyle LVT – nous installons tous types de revêtements avec un savoir-faire artisanal et une précision millimétrique.",
      longDescription:
        "Préparation soignée du support (ragréage, isolation phonique), pose impeccable et finitions parfaites (plinthes, seuils). Intervention dans toute la région bruxelloise et ses environs.",
      features: [
        "Parquet massif et contrecollé",
        "Stratifié haute qualité",
        "Vinyle et LVT design",
        "Ragréage et préparation sol",
        "Pose de plinthes et seuils",
        "Ponçage et vitrification",
      ],
      image: "https://images.unsplash.com/photo-1600607687644-c7171b42498f?w=800",
      gallery: [
        "https://images.unsplash.com/photo-1600607687644-c7171b42498f?w=400",
        "https://images.unsplash.com/photo-1560448075-bb485b1d4e36?w=400",
      ],
      color: "from-purple-500 to-violet-500",
    },
  ];

  const processSteps = [
    { number: "01", title: "Contact", description: "Prenez contact par téléphone, email ou formulaire" },
    { number: "02", title: "Visite", description: "Visite gratuite sur site pour évaluer vos besoins" },
    { number: "03", title: "Devis", description: "Devis détaillé et transparent sous 48h" },
    { number: "04", title: "Planification", description: "Planning des travaux adapté à vos contraintes" },
    { number: "05", title: "Réalisation", description: "Exécution soignée par nos artisans qualifiés" },
    { number: "06", title: "Réception", description: "Vérification finale et garantie de satisfaction" },
  ];

  return (
    <div data-testid="services-page" className="pt-24 bg-[#1A1A1A]">
      {/* Hero Section */}
      <section className="relative bg-[#1A1A1A] py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1771862956369-c840b0a1fc9d?w=1920"
            alt="Rénovation intérieure"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A1A] via-[#1A1A1A]/90 to-transparent" />
        </div>
        
        <div className="absolute top-20 right-20 w-72 h-72 bg-[#E07B39]/20 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block bg-[#E07B39]/20 text-[#E07B39] font-semibold text-sm uppercase tracking-widest px-4 py-2 rounded-full mb-6">
              Nos Expertises
            </span>
            <h1 className="text-5xl sm:text-6xl font-['Playfair_Display'] font-bold text-white mb-6">
              Des Services<br />
              <span className="text-[#E07B39]">d'Excellence</span>
            </h1>
            <p className="text-xl text-[#9A9A9A] leading-relaxed mb-8">
              De la préparation des surfaces aux finitions décoratives les plus raffinées, 
              notre équipe maîtrise l'ensemble des métiers de la rénovation intérieure. 
              Découvrez notre savoir-faire.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/devis">
                <Button className="bg-[#E07B39] hover:bg-[#C86A2E] text-white px-8 py-6 rounded-full">
                  Demander un Devis
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
              <a href="tel:+32488467522">
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#1A1A1A] px-8 py-6 rounded-full">
                  <Phone className="mr-2" size={18} />
                  +32 488 46 75 22
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {services.map((service, index) => (
            <div
              key={service.id}
              id={service.id}
              data-testid={`service-${service.id}`}
              className={`py-20 ${index !== services.length - 1 ? "border-b border-[#E8E8E8]" : ""}`}
            >
              <div className={`grid lg:grid-cols-2 gap-16 items-center ${index % 2 === 1 ? "lg:grid-flow-col-dense" : ""}`}>
                <div className={index % 2 === 1 ? "lg:col-start-2" : ""}>
                  <div className="flex items-center gap-4 mb-6">
                    <div className={`w-14 h-14 bg-gradient-to-r ${service.color} rounded-xl flex items-center justify-center`}>
                      <service.icon className="text-white" size={28} />
                    </div>
                    <span className={`inline-block bg-gradient-to-r ${service.color} bg-clip-text text-transparent font-semibold text-sm uppercase tracking-widest`}>
                      {service.subtitle}
                    </span>
                  </div>
                  <h2 className="text-4xl sm:text-5xl font-['Playfair_Display'] font-bold text-[#1A1A1A] mb-6">
                    {service.title}
                  </h2>
                  <p className="text-lg text-[#6B6B6B] mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  <p className="text-[#6B6B6B] mb-8 leading-relaxed">
                    {service.longDescription}
                  </p>
                  
                  <div className="grid sm:grid-cols-2 gap-3 mb-8">
                    {service.features.map((feature, i) => (
                      <div key={i} className="flex items-center space-x-2">
                        <CheckCircle2 className="text-[#E07B39] flex-shrink-0" size={18} />
                        <span className="text-[#1A1A1A] text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex flex-wrap gap-4">
                    <Link to={`/services/${service.slug}`}>
                      <Button
                        data-testid={`details-btn-${service.id}`}
                        className="bg-[#E07B39] hover:bg-[#C86A2E] text-white rounded-full px-6"
                      >
                        En savoir plus
                        <ArrowRight className="ml-2" size={18} />
                      </Button>
                    </Link>
                    <Link to="/devis">
                      <Button
                        data-testid={`quote-btn-${service.id}`}
                        variant="outline"
                        className="border-[#E07B39] text-[#E07B39] hover:bg-[#E07B39] hover:text-white rounded-full px-6"
                      >
                        Devis Gratuit
                      </Button>
                    </Link>
                  </div>
                </div>
                
                <div className={index % 2 === 1 ? "lg:col-start-1" : ""}>
                  <Link to={`/services/${service.slug}`} className="block group">
                    <div className="relative">
                      <div className="rounded-3xl overflow-hidden shadow-2xl">
                        <LazyImage
                          src={service.image}
                          alt={service.title}
                          className="w-full h-[500px] object-cover group-hover:scale-105 transition-transform duration-500"
                          wrapperClassName="w-full h-[500px]"
                        />
                        <div className={`absolute inset-0 bg-gradient-to-t ${service.color} opacity-20 group-hover:opacity-30 transition-opacity`} />
                      </div>
                      
                      {/* Small gallery images */}
                      <div className="absolute -bottom-8 -right-8 flex gap-2">
                        {service.gallery.map((img, i) => (
                          <div key={i} className="w-24 h-24 rounded-xl overflow-hidden shadow-xl border-4 border-white">
                            <LazyImage 
                              src={img} 
                              alt="" 
                              className="w-full h-full object-cover"
                              wrapperClassName="w-full h-full"
                              showSpinner={false}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block bg-[#E07B39]/10 text-[#E07B39] font-semibold text-sm uppercase tracking-widest px-4 py-2 rounded-full mb-6">
              Notre Processus
            </span>
            <h2 className="text-4xl sm:text-5xl font-['Playfair_Display'] font-bold text-[#1A1A1A] mb-6">
              Comment Nous <span className="text-[#E07B39]">Travaillons</span>
            </h2>
            <p className="text-lg text-[#6B6B6B]">
              Un processus simple et transparent pour des travaux réussis.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {processSteps.map((step, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow relative overflow-hidden group"
              >
                <div className="absolute top-0 right-0 text-[120px] font-['Playfair_Display'] font-bold text-[#F5F5F5] leading-none -translate-y-8 translate-x-4 group-hover:text-[#E07B39]/10 transition-colors">
                  {step.number}
                </div>
                <div className="relative">
                  <div className="w-12 h-12 bg-[#E07B39] rounded-xl flex items-center justify-center mb-6">
                    <span className="text-white font-bold">{step.number}</span>
                  </div>
                  <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#1A1A1A] mb-3">
                    {step.title}
                  </h3>
                  <p className="text-[#6B6B6B]">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block bg-[#E07B39]/20 text-[#E07B39] font-semibold text-sm uppercase tracking-widest px-4 py-2 rounded-full mb-6">
              Nos Engagements
            </span>
            <h2 className="text-4xl sm:text-5xl font-['Playfair_Display'] font-bold text-white mb-6">
              Pourquoi Nous <span className="text-[#E07B39]">Choisir</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Award, title: "Expertise", description: "15+ années d'expérience et des centaines de projets réussis" },
              { icon: Shield, title: "Garantie", description: "Travaux garantis et assurance responsabilité civile" },
              { icon: Clock, title: "Ponctualité", description: "Respect strict des délais annoncés" },
              { icon: Users, title: "Écoute", description: "Un interlocuteur dédié du début à la fin" },
            ].map((item, index) => (
              <div
                key={index}
                className="text-center p-8 border border-white/10 rounded-2xl hover:border-[#E07B39]/50 transition-colors"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[#E07B39] to-[#F59E5E] rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <item.icon className="text-white" size={32} />
                </div>
                <h3 className="font-['Playfair_Display'] text-xl font-semibold text-white mb-3">
                  {item.title}
                </h3>
                <p className="text-[#9A9A9A]">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-r from-[#E07B39] to-[#F59E5E]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-['Playfair_Display'] font-bold text-white mb-6">
            Un Projet en Tête?
          </h2>
          <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Contactez-nous pour discuter de votre projet et obtenir un devis personnalisé gratuit.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/devis">
              <Button
                data-testid="services-cta-quote"
                className="bg-white text-[#E07B39] hover:bg-[#F5F5F5] px-10 py-7 text-lg rounded-full"
              >
                Devis Gratuit
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
            <a href="tel:+32488467522">
              <Button
                data-testid="services-cta-call"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#E07B39] px-10 py-7 text-lg rounded-full"
              >
                <Phone className="mr-2" size={20} />
                +32 488 46 75 22
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
